import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('MarketingAuthorizationService', 'marketing');

export const NMLS_MARKETING_GATE_FLAG = 'nmls_marketing_gate';

export interface MarketingAuthorization {
  governedByRecruiting: boolean;
  authorized: boolean;
  authorizationDate: string | null;
  reason: 'authorized' | 'nmls_pending' | 'not_recruit_linked';
}

export interface MarketingAuthorizationResult {
  gateEnabled: boolean;
  authorization: MarketingAuthorization;
}

type MarketingAuthorizationClient = Pick<typeof supabase, 'from' | 'rpc'>;

const UNGATED_AUTHORIZATION: MarketingAuthorization = {
  governedByRecruiting: false,
  authorized: true,
  authorizationDate: null,
  reason: 'not_recruit_linked'
};

function isMarketingAuthorization(value: unknown): value is MarketingAuthorization {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<MarketingAuthorization>;
  return typeof candidate.governedByRecruiting === 'boolean'
    && typeof candidate.authorized === 'boolean'
    && (candidate.authorizationDate === null || typeof candidate.authorizationDate === 'string')
    && ['authorized', 'nmls_pending', 'not_recruit_linked'].includes(candidate.reason || '');
}

export const marketingAuthorizationService = {
  async getForProfile(
    targetUserId: string,
    isOwnProfile: boolean,
    client: MarketingAuthorizationClient = supabase
  ): Promise<MarketingAuthorizationResult> {
    const { data: flag, error: flagError } = await client
      .from('feature_flags')
      .select('enabled')
      .eq('flag_name', NMLS_MARKETING_GATE_FLAG)
      .maybeSingle();

    if (flagError) {
      log.error('Failed to load the NMLS Marketing gate', { metadata: { targetUserId } }, flagError);
      throw new Error('Unable to verify whether NMLS Marketing controls are enabled');
    }

    if (!flag?.enabled) {
      return {
        gateEnabled: false,
        authorization: UNGATED_AUTHORIZATION
      };
    }

    const { data, error } = isOwnProfile
      ? await client.rpc('current_user_marketing_authorization')
      : await client.rpc('user_marketing_authorization_for_admin', {
          p_user_id: targetUserId
        });

    if (error) {
      log.error('Failed to load NMLS Marketing authorization', {
        metadata: { targetUserId, isOwnProfile }
      }, error);
      throw new Error('Unable to verify NMLS Marketing authorization');
    }

    if (!isMarketingAuthorization(data)) {
      log.error('NMLS Marketing authorization returned an invalid response', {
        metadata: { targetUserId }
      });
      throw new Error('Unable to verify NMLS Marketing authorization');
    }

    return {
      gateEnabled: true,
      authorization: data
    };
  }
};
